import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../utils/axiosSetup.js';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import { closeModal } from '../../../action/modalbehaviour.js';


const EditEmployees = ({employeeId, list}) => {
    const [employeeData, setEmployeeData] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
   
    const fetchEmployee = async (employeeId) => {      
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/users/${employeeId}`)
            .then(response => {
                setEmployeeData(response.data.data);         
            })
            .catch(error => {
                console.log(error);
                setEmployeeData([]); 
            });
    }

    const fetchDepartments = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/department`)
            .then(response => {
                setDepartments(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching departments', error);
            });
    };

    const fetchRoles = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/role`)
            .then(response => {
                setRoles(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching roles', error);
            });
    };

    useEffect(() => {
        if (employeeId) {
            fetchEmployee(employeeId);
        }
        fetchDepartments();
        fetchRoles();
    },[employeeId]);

    // VALIDATION RULES
    const schema = Yup.object().shape({
            emp_id: Yup.number().required('employee id is a required field').typeError('employee id must be a number'),
            name: Yup.string().required(),
            email: Yup.string().email()
                                  .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, 'Email must be a valid email')
                                  .required('Email is a required field'),
            dob: Yup.date().nullable().typeError('dob is a required field')
                .required('dob is a required field').max(new Date(), 'date of birth cannot be in the future'),
            joined_date: Yup.string().required('joining date is a required field'),
            designation: Yup.string().required(),
            department: Yup.string().required(),
            role: Yup.string().required(),
            phone_number: Yup.string().nullable().required('phone is a required field')
                            .matches(/^\d{10}$/, 'phone must be exactly 10 digits'),
            home_phone: Yup.string().nullable().notRequired()
                            .when('home_phone', {
                                is: (value) => value?.length,
                                then: (rule) => rule.matches(/^\d{10}$/, 'emergency phone must be exactly 10 digits'),
                            }),
            personal_email: Yup.string().nullable().notRequired()
                                        .when('personal_email', {
                                            is: (value) => value?.length,
                                            then: (rule) => rule.email('personal email must be a valid email').matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, 'personal email must be a valid email'),
                                        }),
        },[
            ['home_phone', 'home_phone'],
            ['personal_email', 'personal_email']
        ]
    );
    
    const { register, handleSubmit, formState: { errors }, reset} = useForm({
        mode: 'all',
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (employeeData) {
            reset(employeeData);
        }
    }, [employeeData, reset]);

    const onSubmit = async (data) => {   
        const formData = {
            ...data,
            dob: data.dob.toLocaleDateString('en-CA')
        };
            
        axiosInstance.patch(`${process.env.REACT_APP_API_URL}/users/${employeeId}`, formData)
            .then(response => {
                Swal.fire({icon:"success", text:response.data.message});
                closeModal(`edit_employee_${employeeId}`);
                list();
            })
            .catch(({response:{data}})=>{
                Swal.fire({text:data.message, icon:"error"});
                console.error('Error updating employees', data);
            });
    }

    if (!employeeId || !employeeData) {
        return null;
    }

    return (
        <div id={`edit_employee_${employeeId}`} className="modal custom-modal fade" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Employee</h5>                        
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">                                
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Employee ID <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" defaultValue={employeeData.emp_id} {...register('emp_id')} />
                                        <div className="text-sm text-danger">
                                            {errors.emp_id && <p>{errors.emp_id.message}</p>}
                                        </div>
                                    </div>
                                </div>                                
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Email <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" defaultValue={employeeData.email} {...register('email')} />
                                        <div className="text-sm text-danger">
                                            {errors.email && <p>{errors.email.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Name <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" defaultValue={employeeData.name}  {...register('name')} />
                                        <div className="text-sm text-danger">
                                            {errors.name && <p>{errors.name.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Joining Date <span className="text-danger">*</span></label>
                                        <input className="form-control" type="date" defaultValue={employeeData.joined_date} {...register('joined_date')} />
                                        <div className="text-sm text-danger">
                                            {errors.joined_date && <p>{errors.joined_date.message}</p>}
                                        </div>
                                    </div>
                                </div>                                
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Designation <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" defaultValue={employeeData.designation} {...register('designation')} />
                                        <div className="text-sm text-danger">
                                            {errors.designation && <p>{errors.designation.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Date of Birth <span className="text-danger">*</span></label>
                                        <input className="form-control" type="date" defaultValue={employeeData.dob || ''} {...register('dob')} />
                                        <div className="text-sm text-danger">
                                            {errors.dob && <p>{errors.dob.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Department <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('department', {value: employeeData.department_id || ""  })}>
                                            <option value="">Select Department</option>
                                            {departments.map(dep => (
                                                <option key={dep.id} value={dep.id}>{dep.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.department && <p>{errors.department.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Role <span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('role',{value: employeeData.role_id || ""})}>
                                            <option value="">Select Role</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.role && <p>{errors.role.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Address</label>
                                        <textarea className="form-control" type="text" {...register('address')} >{employeeData.address || ""}</textarea>
                                    </div>
                                </div>                                
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Personal Phone <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" defaultValue={employeeData.phone_number || ""} {...register('phone_number')} />
                                        <div className="text-sm text-danger">
                                            {errors.phone_number && <p>{errors.phone_number.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Personal Email </label>
                                        <input className="form-control" type="text" defaultValue={employeeData.personal_email || ""} {...register('personal_email')} />
                                        <div className="text-sm text-danger">
                                            {errors.personal_email && <p>{errors.personal_email.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Emergency Phone </label>
                                        <input className="form-control" type="text" defaultValue={employeeData.home_phone || ""} {...register('home_phone')} />                                    
                                        <div className="text-sm text-danger">
                                            {errors.home_phone && <p>{errors.home_phone.message}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="submit-section">
                                <button type="submit" className="btn btn-primary submit-btn">UPDATE</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditEmployees